import React, { useEffect, useState } from 'react';
import { useLocation, useRoute } from 'wouter';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton
} from '@mui/material';
import { supabase } from '../../supabaseClient';
import FileUpload from '@/components/ui/fileupload';


const VendorDetails = () => {
  const [match, params] = useRoute('/vendor-details/:vendorId');
  const [, navigate] = useLocation();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vendor, setVendor] = useState(null);
  const [agencyName, setAgencyName] = useState(null); 
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false); 
  const [formValues, setFormValues] = useState({ name: '', phone: '', email: '', commissionPercentage: '',websiteURL:'',industry: '',address: '',status:'',requiredSkills:'',contractStartDate:'',contractEndDate:'',relationshipManager: '',agency_id: '',});
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const vendorId = params?.vendorId;
  const [availableAgencies, setAvailableAgencies] = useState([]);

  useEffect(() => {
    const fetchVendorDetails = async () => {
      if (!vendorId) {
        setError('Vendor ID is missing.');
        setLoading(false);
        return;
      }

      try {
        const { data: vendorData, error: vendorError } = await supabase
          .from('vendors')
          .select('*')
          .eq('id', vendorId)
          .single();
        if (vendorError) {
          setError(vendorError.message);
        } else {
          setVendor(vendorData);
          setFormValues({
            name: vendorData.name,
            phone: vendorData.phone,
            email: vendorData.email,
            commissionPercentage: vendorData.commissionPercentage,
            websiteURL: vendorData.websiteURL,
            industry: vendorData.industry,
            address: vendorData.address,
            status: vendorData.status,
            requiredSkills: vendorData.requiredSkills,
            contractStartDate: vendorData.contractStartDate,
            contractEndDate: vendorData.contractEndDate,
            relationshipManager: vendorData.relationshipManager,
            agency_id: vendorData.agency_id,
          });
          

          // Fetch agency name using agency_id
          if (vendorData?.agency_id) {
            const { data: agencyData, error: agencyError } = await supabase
              .from('agency')
              .select('name')
              .eq('id', vendorData.agency_id)
              .single();

            if (agencyError) {
              setError(agencyError.message);
            } else {
              setAgencyName(agencyData?.name || 'Unknown Agency');
            }
          }
        }

        const { data, error } = await supabase
          .from('projects')
          .select('*')
          .eq('vendor_id', vendorId);

        if (error) {
          setError(error.message);
        } else {
          setRoles(data || []);
        }
      } catch (err) {
        setError('Error fetching vendor details');
      } finally {
        setLoading(false);
      }
    };

    const fetchAgencies = async () => {
      try {
        const { data, error } = await supabase.from('agency').select('id, name');
        if (error) {
          setError(error.message);
        } else {
          setAvailableAgencies(data || []);
        }
      } catch (err) {
        setError('Error fetching agencies.');
      }
    };
  

    fetchVendorDetails();
    fetchAgencies();
  }, [vendorId]);

  const handleBack = () => window.history.back();

  const handleRoleClick = (role) => {
    if (role?.id) {
      navigate(`/role-details/${role.id}`);
    }
  };

  const handleAgencyClick = () => {
    if (vendor?.agency_id) {
      navigate(`/agency-details/${vendor.agency_id}`);
    }
  };

  // Open the dialog for editing vendor details
  const handleEditClick = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  const handleDialogOpen = () => setDialogOpen(true);
  const handleDialogClose = () => setDialogOpen(false);

  const handleUpdate = async () => {
    try {
      const updatedValues = {
        name: formValues.name,
        phone: formValues.phone,
        email: formValues.email,
        commissionPercentage: formValues.commissionPercentage
          ? parseFloat(formValues.commissionPercentage)
          : null,
        websiteURL: formValues.websiteURL,
        industry: formValues.industry,
        address: formValues.address,
        status: formValues.status,
        requiredSkills: formValues.requiredSkills,
        contractStartDate: formValues.contractStartDate? formValues.contractStartDate.trim() : null,
        contractEndDate: formValues.contractEndDate? formValues.contractEndDate.trim() : null,
        relationshipManager: formValues.relationshipManager,
        agency_id: formValues.agency_id || null
      };
  
      const { data, error } = await supabase
        .from('vendors')
        .update(updatedValues)
        .eq('id', vendorId)
        .select();
  
      if (error) {
        setError(error.message);
      } else {
        setVendor(data[0]);
        setDialogOpen(false);
        window.location.reload();
      }
    } catch (err) {
      setError('Error updating Vendor details.');
    }
  };
  

    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormValues({ ...formValues, [name]: value });
    };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  if (!vendor) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography>No vendor details found.</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
          Vendor Details
        </Typography>
        <Box>
          <Button
                      variant="contained"
                      onClick={handleDialogOpen}
                      sx={{
                        backgroundColor: 'black',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#333',
                        },
                        mr: 1,
                      }}
                    >
                      Update
                    </Button>
          <Button
            variant="contained"
            onClick={handleBack}
            sx={{
              backgroundColor: 'black',
              color: 'white',
              '&:hover': {
                backgroundColor: '#333',
              },
            }}
          >
            Back
          </Button>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            {vendor?.name}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Agency:
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={handleAgencyClick}
              >
                {agencyName || 'Unknown Agency'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Contact Email:
              </Typography>
              <Typography variant="body1">{vendor.email}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Industry:
              </Typography>
              <Typography variant="body1">{vendor.industry}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Required Skills:
              </Typography>
              <Typography variant="body1">{vendor.requiredSkills}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Website URL:
              </Typography>
              <Typography variant="body1">
                <a
                  href={vendor.websiteURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#1976d2', textDecoration: 'none' }}
                >
                  {vendor.websiteURL}
                </a>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Contract Start Date:
              </Typography>
              <Typography variant="body1">{vendor.contractStartDate}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Contract End Date:
              </Typography>
              <Typography variant="body1">{vendor.contractEndDate}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Commission Percentage:
              </Typography>
              <Typography variant="body1">{vendor.commissionPercentage}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Relationship Manager:
              </Typography>
              <Typography variant="body1">{vendor.relationshipManager}</Typography>
            </Grid>
          </Grid>

          <Divider sx={{ mt: 4 }} />

          <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
            Roles Posted by {vendor.name}
          </Typography>

          <List>
            {roles.length === 0 ? (
              <Typography>No Roles found for this vendor.</Typography>
            ) : (
              roles.map((role, index) => 
                role && role.title ? (  
                  <ListItem key={index} button onClick={() => handleRoleClick(role)}>
                    <ListItemText
                      primary={role.title}
                      secondary={`Start Date: ${role.job_posted_date} | End Date: ${role.job_apply_by_date}`}
                    />
                  </ListItem>
                ) : null
              )
            )}
          </List>
          <FileUpload basepath={'vendor/'+vendor.id+'/'}/>
        </CardContent>
      </Card>

    
          {/* Update Dialog */}
                <Dialog open={dialogOpen} onClose={handleDialogClose} fullWidth maxWidth="sm">
                  <DialogTitle>Update Role Details</DialogTitle>
                   <DialogContent>
                  
                <TextField
                  select
                  label="Agency"
                  name="agency_id"
                  fullWidth
                  margin="normal"
                  value={formValues.agency_id || ''}
                  onChange={handleInputChange}
                  SelectProps={{
                    native: true,
                  }}
                >
                  <option value="" disabled>
                    Select an Agency
                  </option>
                  {availableAgencies.map((agency) => (
                    <option key={agency.id} value={agency.id}>
                      {agency.name}
                    </option>
                  ))}
                </TextField>

                    <TextField
                        label="Name"
                        name="name"
                        type="text"
                        fullWidth
                        margin="normal"
                        value={formValues.name}
                        onChange={handleInputChange}
                      />

                      <TextField
                        label="Phone"
                        name="phone"
                        type="text"
                        fullWidth
                        margin="normal"
                        value={formValues.phone}
                        onChange={handleInputChange}
                      />

                      <TextField
                        label="Email"
                        name="email"
                        type="email"
                        fullWidth
                        margin="normal"
                        value={formValues.email}
                        onChange={handleInputChange}
                      />

                      <TextField
                        label="Commission Percentage"
                        name="commissionPercentage"
                        type="number"
                        fullWidth
                        margin="normal"
                        value={formValues.commissionPercentage}
                        onChange={handleInputChange}
                      />

                      <TextField
                        label="Website URL"
                        name="websiteURL"
                        type="url"
                        fullWidth
                        margin="normal"
                        value={formValues.websiteURL}
                        onChange={handleInputChange}
                      />

                      <TextField
                        label="Industry"
                        name="industry"
                        type="text"
                        fullWidth
                        margin="normal"
                        value={formValues.industry}
                        onChange={handleInputChange}
                      />

                      <TextField
                        label="Address"
                        name="address"
                        type="text"
                        fullWidth
                        margin="normal"
                        value={formValues.address}
                        onChange={handleInputChange}
                      />

                      <TextField
                        label="Status"
                        name="status"
                        type="text"
                        fullWidth
                        margin="normal"
                        value={formValues.status}
                        onChange={handleInputChange}
                      />

                      <TextField
                        label="Required Skills"
                        name="requiredSkills"
                        type="text"
                        fullWidth
                        margin="normal"
                        value={formValues.requiredSkills}
                        onChange={handleInputChange}
                      />

                      <TextField
                        label="Contract Start Date"
                        name="contractStartDate"
                        type="date"
                        fullWidth
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                        value={formValues.contractStartDate}
                        onChange={handleInputChange}
                      />

                      <TextField
                        label="Contract End Date"
                        name="contractEndDate"
                        type="date"
                        fullWidth
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                        value={formValues.contractEndDate}
                        onChange={handleInputChange}
                      />

                      <TextField
                        label="Relationship Manager"
                        name="relationshipManager"
                        type="text"
                        fullWidth
                        margin="normal"
                        value={formValues.relationshipManager}
                        onChange={handleInputChange}
                      />
                                        </DialogContent>
                  <DialogActions>
                    <Button onClick={handleDialogClose} color="secondary"
                     sx={{
                      backgroundColor: 'black',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#333',
                      },
                      mr: 1,
                    }}>
                      Cancel
                    </Button>
                    <Button onClick={handleUpdate} color="primary" variant="contained"
                     sx={{
                      backgroundColor: 'black',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#333',
                      },
                      mr: 1,
                    }}>
                      Save
                    </Button>
                  </DialogActions>
                </Dialog>
    </Container>
  );
};

export default VendorDetails;
